'use client';

import { BackupStatus } from '@repo/api-gw-sdk';
import { useMemo } from 'react';
import { useIsUnsupportedStatusOffered } from '@/app/(dashboard)/inventory/inventorySettingsContext';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags, useLaunchDarkly } from '@/contexts/useFeatureFlags';

// The order the filter bar offers them in. The last four are the statuses the
// inventory leaves out of its default scope; picking one here supersedes that
// baseline.
const FILTERABLE_BACKUP_STATUSES: BackupStatus[] = [BackupStatus.Protected, BackupStatus.Dlsg, BackupStatus.AllViolationsMuted, BackupStatus.ViolationsDetected, BackupStatus.ActionRequired, BackupStatus.GenericBackups, BackupStatus.NotBackedUp, BackupStatus.ClassificationFailed, BackupStatus.InitialClassification, BackupStatus.Terminated, BackupStatus.Disconnected, BackupStatus.ExcludedFromBackup, BackupStatus.Unsupported, BackupStatus.LimitExceeded];

/**
 * The backup statuses this account can currently see, in the order above. Dlsg needs
 * its flag, the muted/violations statuses need controls entitlement — an async
 * DAL call — so `isLoading` stays true until both answers are in, letting callers
 * that only get one shot at a default (e.g. a query committed once on mount)
 * wait rather than bake in a guess.
 */
export const useAvailableBackupStatuses = (): {
  statuses: BackupStatus[];
  isLoading: boolean;
} => {
  const {
    dlsgFrontend
  } = useFeatureFlags();
  const {
    isReady: flagsReady
  } = useLaunchDarkly();
  const isUnsupportedOffered = useIsUnsupportedStatusOffered();
  const dal = useDAL();
  const {
    data: hasControls,
    isLoading: controlsLoading
  } = dal.controls.useHasControls();

  // Memoized on the three answers that gate the list rather than rebuilt per
  // render: a caller derives its one-shot default query from this array, and a
  // fresh identity each render would defeat the memo holding that default
  // steady.
  const statuses = useMemo(() => {
    const gatedStatuses = [...(dlsgFrontend ? [] : [BackupStatus.Dlsg]), ...(hasControls ? [] : [BackupStatus.AllViolationsMuted, BackupStatus.ViolationsDetected]),
    // Filtering by a status the inventory keeps out of view would contradict
    // the scope it is applying, so the status is offered only once the
    // inventory settings turn unsupported resources on.
    ...(isUnsupportedOffered ? [] : [BackupStatus.Unsupported])];
    return FILTERABLE_BACKUP_STATUSES.filter(status => !gatedStatuses.includes(status));
  }, [dlsgFrontend, hasControls, isUnsupportedOffered]);
  return {
    statuses,
    isLoading: !flagsReady || controlsLoading
  };
};